import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card } from "antd";
import CountUp from "react-countup";
import { findPeakBucket } from "functions/admin/mockPeakLoadChartData";

const teal = "#32B7C2";
const createdColor = "#2a8e57";
const paidColor = "#1e7a6a";
const gridColor = "#c4e8ec";

const PeakLoadChart = ({ report, mode }) => {
  const data = useMemo(() => report?.buckets ?? [], [report]);
  const rangeLabel = report?.rangeLabel ?? "—";
  const hasData = data.some(
    (row) => row.createdCount > 0 || row.paidCount > 0
  );

  const peakCreated = useMemo(
    () => report?.peak?.created ?? findPeakBucket(data, "createdCount"),
    [report, data]
  );
  const peakPaid = useMemo(
    () => report?.peak?.paid ?? findPeakBucket(data, "paidCount"),
    [report, data]
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-md text-sm">
        <div className="font-semibold text-[#438c7c] mb-2">{label} (УБ)</div>
        {payload.map((item) => (
          <div key={item.dataKey} style={{ color: item.color }} className="font-medium">
            {item.name}:{" "}
            <CountUp end={item.value} separator="," duration={0.4} />
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card
      className="w-full rounded-2xl shadow-lg border-0"
      bodyStyle={{ padding: 0 }}
    >
      <div className="p-6 sm:p-8 rounded-t-2xl bg-white border-b border-gray-100">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
          <div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900 tracking-wide">
              Оргил ачаалал
            </div>
            <div className="text-gray-500 text-sm mt-1 max-w-xl">
              {rangeLabel}
              {mode === "yesterday"
                ? " · өмнөх өдөр, цаг бүр"
                : " · 14 хоног, цагаар нэгтгэсэн"}
            </div>
          </div>
          <div className="flex flex-wrap gap-8">
            <div>
              <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                Оргил — төлбөр
              </div>
              <div className="text-lg font-bold text-gray-900">
                {peakPaid ? peakPaid.label : "—"}
              </div>
              <div className="text-sm font-semibold" style={{ color: paidColor }}>
                {peakPaid ? (
                  <>
                    <CountUp end={peakPaid.count} separator="," /> нэхэмжлэл
                  </>
                ) : (
                  "—"
                )}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                Оргил — үүсэлт
              </div>
              <div className="text-lg font-bold text-gray-900">
                {peakCreated ? peakCreated.label : "—"}
              </div>
              <div className="text-sm font-semibold" style={{ color: createdColor }}>
                {peakCreated ? (
                  <>
                    <CountUp end={peakCreated.count} separator="," /> нэхэмжлэл
                  </>
                ) : (
                  "—"
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 sm:p-8 pt-4 rounded-b-2xl bg-gradient-to-b from-white via-[#e8f6f8] to-[#d1ebe5]">
        {!hasData && report && (
          <p className="text-center text-gray-500 py-8 mb-0">
            Сонгосон хугацаанд өгөгдөл алга.
          </p>
        )}

        {(hasData || !report) && (
          <ResponsiveContainer width="100%" height={360}>
            <BarChart
              data={data}
              margin={{ top: 16, right: 16, left: 0, bottom: 8 }}
              barCategoryGap="18%"
              barGap={4}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} />
              <XAxis
                dataKey="label"
                axisLine={{ stroke: "#97d1c4" }}
                tickLine={false}
                tick={{ fill: "#438c7c", fontSize: 11, fontWeight: 600 }}
                interval={1}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: teal, fontSize: 12, fontWeight: 600 }}
                width={48}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: 12 }}
                formatter={(value) => (
                  <span className="text-sm font-semibold text-[#438c7c]">{value}</span>
                )}
              />
              <ReferenceLine
                x="11:00"
                stroke="#f59e0b"
                strokeDasharray="4 4"
                label={{
                  value: "Settle",
                  position: "top",
                  fill: "#b45309",
                  fontSize: 11,
                }}
              />
              <Bar
                name="Нэхэмжлэл үүссэн"
                dataKey="createdCount"
                fill="#78c2b7"
                radius={[6, 6, 0, 0]}
                maxBarSize={14}
              />
              <Bar
                name="Төлбөр батлагдсан"
                dataKey="paidCount"
                fill="#46ce7c"
                radius={[6, 6, 0, 0]}
                maxBarSize={14}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default PeakLoadChart;
